import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

import {
  NR_ACCOUNT_ID,
  NR_LICENSE_KEY,
  NR_NON_PROD_APPLICATION_ID,
  NR_PROD_APPLICATION_ID,
  hasPlaceholderCredentials,
} from './constants';

let agentInstance: BrowserAgent | undefined;

// Local/dev hosts we never instrument: dev traffic would otherwise stream into the
// shared NR application and contaminate the per-dealer metrics. Deployed environments
// (prod, staging, demo) all fall through and ARE instrumented.
function isLocalHost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0' ||
    hostname === '[::1]' ||
    hostname === '::1' ||
    hostname.endsWith('.localhost') ||
    hostname.endsWith('.local') ||
    // RFC1918 private ranges — a dev server opened from a phone on the LAN.
    /^10\./.test(hostname) ||
    /^192\.168\./.test(hostname) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(hostname)
  );
}

// Reports a handled error to New Relic. No-ops when the agent was not
// initialized, so callers don't need to guard.
export function noticeError(error: Error): void {
  agentInstance?.noticeError(error);
}

// Records a page-action event. No-ops when the agent was not initialized,
// so callers don't need to guard.
//
// NOTE ON QUERYING: this is the browser agent's PageAction API, so events land in
// the `PageAction` event type with `name` in the `actionName` attribute — NOT in an
// event type named after `name`. Query as:
//   FROM PageAction SELECT ... WHERE actionName = 'QuoteSubmission'
export function recordCustomEvent(name: string, attributes: Record<string, unknown>): void {
  agentInstance?.addPageAction(name, attributes);
}

export function initNewRelic(apiUrl: string, envTarget: string, dealer: string): void {
  if (isLocalHost(window.location.hostname)) {
    return;
  }

  const applicationId =
    envTarget === 'production' ? NR_PROD_APPLICATION_ID : NR_NON_PROD_APPLICATION_ID;

  if (hasPlaceholderCredentials(NR_LICENSE_KEY, NR_ACCOUNT_ID, applicationId)) {
    return;
  }

  // Idempotent: a second agent would double-report every event.
  if (agentInstance) {
    return;
  }

  const apiOrigin = new URL(apiUrl).origin;

  const agent = new BrowserAgent({
    init: {
      distributed_tracing: {
        enabled: true,
        allowed_origins: [apiOrigin],
      },
      // false: agent 1.321.0 writes no cookies either way, but this also suppresses the
      // first-party NRBA_SESSION localStorage write. Session stitching is not needed for
      // the submission metrics this app records. Revisit if mid-review abandonment
      // (PRD SM-C1) is ever implemented — that does need session identity.
      privacy: { cookies_enabled: false },
      ajax: { deny_list: ['bam.nr-data.net'] },
      performance: {
        capture_detail: false,
        capture_marks: false,
        capture_measures: true,
      },
    },
    info: {
      beacon: 'bam.nr-data.net',
      errorBeacon: 'bam.nr-data.net',
      licenseKey: NR_LICENSE_KEY,
      applicationID: applicationId,
      sa: 1,
    },
    loader_config: {
      accountID: NR_ACCOUNT_ID,
      trustKey: NR_ACCOUNT_ID,
      agentID: applicationId,
      licenseKey: NR_LICENSE_KEY,
      applicationID: applicationId,
    },
  });

  agent.setCustomAttribute('dealer', dealer);
  agentInstance = agent;
}
